import { ArrowLeft, ClipboardList, Pencil, Plus } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { PlatformBadge } from "@/components/competitors/PlatformIcon";
import { IconAction, SoftChip } from "@/components/competitors/ui";
import { EmptyState, LoadingCards } from "@/components/market-intel/components";
import { formatShortDate, toIsoDay } from "@/components/market-intel/dates";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { planStatusLabelKey } from "./constants";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import { PieceRow } from "./PieceRow";
import { PlanForm } from "./PlanForm";
import {
  phaseOfWeek,
  piecesByWeek,
  planProgress,
  planWeekCount,
  planWeekOf,
  sortPhases,
  sortPillars,
} from "./plans";
import { marketingTabPath, planPath } from "./routes";
import type { ContentPlan } from "./types";
import { PillarChips } from "./ui";

const STATUS_TONE: Record<ContentPlan["status"], string> = {
  borrador: "bg-slate-500/12 text-slate-700 dark:text-slate-300",
  activo:
    "bg-emerald-500/14 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200",
  finalizado: "bg-muted text-muted-foreground",
};

const PlanStatusBadge = ({ status }: { status: ContentPlan["status"] }) => {
  const translate = useTranslate();
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold",
        STATUS_TONE[status] ?? STATUS_TONE.activo,
      )}
    >
      {translate(planStatusLabelKey(status))}
    </span>
  );
};

const usePeriod = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  return (plan: ContentPlan): string => {
    if (!plan.start_date && !plan.end_date)
      return translate("marketing.plans.no_period");
    const range = [plan.start_date, plan.end_date]
      .map((day) => (day ? formatShortDate(day, locale) : "…"))
      .join(" – ");
    const weeks = planWeekCount(plan);
    return weeks
      ? `${range} · ${translate("marketing.plans.weeks", { smart_count: weeks })}`
      : range;
  };
};

/** The plan's progress bar: published pieces over planned ones. */
const Progress = ({ plan }: { plan: ContentPlan }) => {
  const translate = useTranslate();
  const { data } = useMarketing();
  const progress = planProgress(plan, data.items, data.cadences);
  const planned = Math.max(progress.total, progress.target ?? 0);
  const label = translate("marketing.plans.progress_value", {
    published: progress.published,
    planned,
  });
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-muted-foreground">
          {translate("marketing.plans.progress")}
        </span>
        <span className="font-semibold tabular-nums">{label}</span>
      </div>
      <div
        className="mk-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={planned}
        aria-valuenow={progress.published}
        aria-label={label}
      >
        <span
          className="mk-bar__fill mk-bar__fill--success"
          style={{ "--mk-bar": `${progress.ratio * 100}%` } as CSSProperties}
        />
      </div>
      {progress.target ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {translate("marketing.plans.target", { count: progress.target })}
        </p>
      ) : null}
    </div>
  );
};

const Cadence = ({ plan }: { plan: ContentPlan }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data } = useMarketing();
  const rows = data.cadences.filter(
    (row) => String(row.plan_id) === String(plan.id),
  );
  if (!rows.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((row) => {
        const channel = findById(data.channels, row.channel_id);
        return (
          <span
            key={row.id}
            className="inline-flex h-7 items-center gap-1.5 rounded-full bg-muted pr-2.5 pl-0.5 text-xs font-semibold"
          >
            {channel ? (
              <PlatformBadge platform={channel.platform} size="sm" />
            ) : null}
            <span className="truncate">{channel?.name}</span>
            <span className="text-muted-foreground tabular-nums">
              {translate("marketing.plans.per_week", {
                count: Number(row.posts_per_week).toLocaleString(locale),
              })}
            </span>
          </span>
        );
      })}
    </div>
  );
};

/** Marketing > Planes: every plan with its period, pillars and progress. */
export const PlansView = ({ isPending }: { isPending: boolean }) => {
  const translate = useTranslate();
  const { data } = useMarketing();
  const period = usePeriod();
  const [formOpen, setFormOpen] = useState(false);

  if (isPending) return <LoadingCards />;
  return (
    <section className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">
          {translate("marketing.plans.heading")}
        </h2>
        <Button
          type="button"
          className="btn-save h-9 rounded-full px-4"
          onClick={() => setFormOpen(true)}
        >
          <Plus aria-hidden className="size-4" />
          {translate("marketing.plans.new")}
        </Button>
      </div>
      {!data.plans.length ? (
        <EmptyState
          icon={ClipboardList}
          title={translate("marketing.plans.empty")}
          hint={translate("marketing.plans.empty_hint")}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2">
          {data.plans.map((plan, index) => {
            const pillars = sortPillars(
              data.pillars.filter(
                (pillar) => String(pillar.plan_id) === String(plan.id),
              ),
            );
            const phases = sortPhases(
              data.phases.filter(
                (phase) => String(phase.plan_id) === String(plan.id),
              ),
            );
            return (
              <li
                key={plan.id}
                className="mi-card mi-rise"
                data-interactive
                style={{ "--mi-index": index } as CSSProperties}
              >
                <Link
                  to={planPath(plan.id)}
                  className="flex h-full flex-col gap-3 rounded-[inherit] p-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 md:p-5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[17px] leading-snug font-semibold [overflow-wrap:anywhere]">
                      {plan.name}
                    </h3>
                    <PlanStatusBadge status={plan.status} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {period(plan)}
                  </p>
                  {plan.objective ? (
                    <p className="line-clamp-3 text-sm">{plan.objective}</p>
                  ) : null}
                  {pillars.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      <PillarChips
                        pillars={pillars}
                        siblings={pillars}
                        withName
                      />
                    </div>
                  ) : null}
                  {phases.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {phases.map((phase) => (
                        <SoftChip key={phase.id}>
                          {phaseLabel(phase, translate)}
                        </SoftChip>
                      ))}
                    </div>
                  ) : null}
                  <Cadence plan={plan} />
                  <div className="mt-auto">
                    <Progress plan={plan} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <PlanForm open={formOpen} onOpenChange={setFormOpen} plan={null} />
    </section>
  );
};

const phaseLabel = (
  phase: {
    number: number;
    name?: string | null;
    start_week?: number | null;
    end_week?: number | null;
  },
  translate: ReturnType<typeof useTranslate>,
): string =>
  [
    translate("marketing.plans.phase_name", { number: phase.number }),
    phase.name,
    phase.start_week != null && phase.end_week != null
      ? translate("marketing.plans.phase_weeks", {
          from: phase.start_week,
          to: phase.end_week,
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** A plan's page: header, pillars, phases, cadence and its pieces by week. */
export const PlanDetail = ({ isPending }: { isPending: boolean }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, newPiece } = useMarketing();
  const period = usePeriod();
  const [formOpen, setFormOpen] = useState(false);
  const plan = findById(data.plans, id);

  if (isPending) return <LoadingCards />;
  if (!plan)
    return (
      <EmptyState
        icon={ClipboardList}
        title={translate("marketing.plans.not_found")}
        action={
          <Button
            nativeButton={false}
            render={<Link to={marketingTabPath("plans")} />}
            variant="outline"
            className="rounded-full"
          >
            {translate("marketing.plans.back")}
          </Button>
        }
      />
    );

  const pillars = sortPillars(
    data.pillars.filter((pillar) => String(pillar.plan_id) === String(plan.id)),
  );
  const phases = sortPhases(
    data.phases.filter((phase) => String(phase.plan_id) === String(plan.id)),
  );
  const weeks = piecesByWeek(plan, data.items);
  const currentWeek = planWeekOf(plan, toIsoDay(new Date()));

  return (
    <section className="space-y-4">
      <Link to={marketingTabPath("plans")} className="mk-link">
        <ArrowLeft aria-hidden className="size-4" />
        {translate("marketing.plans.back")}
      </Link>
      <div className="mi-card mi-rise space-y-3 p-4 md:p-5">
        <div className="flex items-start gap-2">
          <h2 className="min-w-0 flex-1 text-xl leading-snug font-semibold tracking-tight [overflow-wrap:anywhere]">
            {plan.name}
          </h2>
          <PlanStatusBadge status={plan.status} />
          <IconAction
            label={translate("marketing.plans.edit")}
            icon={Pencil}
            onClick={() => setFormOpen(true)}
          />
        </div>
        <p className="text-sm text-muted-foreground">{period(plan)}</p>
        {plan.objective ? <p className="text-sm">{plan.objective}</p> : null}
        <Cadence plan={plan} />
        <Progress plan={plan} />
        {plan.notes ? (
          <p className="rounded-xl bg-muted/60 p-3 text-sm text-muted-foreground">
            {plan.notes}
          </p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {pillars.length ? (
          <div className="mi-card p-4 md:p-5">
            <h3 className="mb-3 text-[17px] font-semibold">
              {translate("marketing.plans.pillars")}
            </h3>
            <ul className="flex flex-col gap-3">
              {pillars.map((pillar) => (
                <li key={pillar.id} className="flex items-start gap-2.5">
                  <PillarChips pillars={[pillar]} siblings={pillars} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{pillar.name}</p>
                    {pillar.description ? (
                      <p className="text-sm text-muted-foreground">
                        {pillar.description}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {phases.length ? (
          <div className="mi-card p-4 md:p-5">
            <h3 className="mb-3 text-[17px] font-semibold">
              {translate("marketing.plans.phases")}
            </h3>
            <ol className="flex flex-col gap-3">
              {phases.map((phase) => (
                <li key={phase.id} className="space-y-0.5">
                  <p className="text-sm font-semibold">
                    {phaseLabel(phase, translate)}
                  </p>
                  {phase.description ? (
                    <p className="text-sm text-muted-foreground">
                      {phase.description}
                    </p>
                  ) : null}
                  {phase.cadence ? (
                    <p className="text-sm">
                      <SoftChip>{phase.cadence}</SoftChip>
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold tracking-tight">
          {translate("marketing.plans.pieces")}
        </h3>
        <Button
          type="button"
          variant="ghost"
          className="h-9 rounded-full bg-primary/8 px-3.5 text-sm font-semibold text-primary dark:bg-primary/15"
          onClick={() => newPiece({ plan_id: String(plan.id) })}
        >
          <Plus aria-hidden className="size-4" />
          {translate("marketing.common.new_piece")}
        </Button>
      </div>
      {weeks.length ? (
        <div className="flex flex-col gap-3">
          {weeks.map((group) => {
            const phase = phaseOfWeek(phases, group.week);
            return (
              <section
                key={group.week}
                className="mi-card px-4 py-3"
                data-current={group.week === currentWeek ? "true" : undefined}
                aria-label={
                  group.week
                    ? translate("marketing.common.week_long", {
                        week: group.week,
                      })
                    : translate("marketing.plans.no_week")
                }
              >
                <header className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold">
                    {group.week
                      ? translate("marketing.common.week_long", {
                          week: group.week,
                        })
                      : translate("marketing.plans.no_week")}
                  </h4>
                  {phase ? (
                    <span className="text-xs text-muted-foreground">
                      {translate("marketing.plans.phase_name", {
                        number: phase.number,
                      })}
                      {phase.name ? ` · ${phase.name}` : ""}
                    </span>
                  ) : null}
                  {group.week === currentWeek ? (
                    <span className="mk-current">
                      {translate("marketing.plans.current_week")}
                    </span>
                  ) : null}
                </header>
                <ul className="-mx-1 flex flex-col">
                  {group.items.map((item, index) => (
                    <PieceRow key={item.id} piece={item} index={index} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : (
        <p className="mi-card p-6 text-center text-sm text-muted-foreground">
          {translate("marketing.plans.no_pieces")}
        </p>
      )}
      <PlanForm
        open={formOpen}
        onOpenChange={setFormOpen}
        plan={plan}
        onDeleted={() => navigate(marketingTabPath("plans"))}
      />
    </section>
  );
};
